import React from 'react';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

/**
 * Tenant switcher. Rendered in the sidebar.
 *
 * - For super-admins: dropdown to switch between all active tenants.
 * - For regular admins with a single tenant: read-only badge.
 * - For regular admins with multiple tenants: dropdown to switch.
 * - Hidden when no tenants are available (e.g. pre-login or loading).
 */
export default function TenantSwitcher() {
  const { tenants, activeTenant, isSuperAdmin, switchTenant, loading } = useTenant();

  if (loading || !tenants || tenants.length === 0) return null;

  const canSwitch = isSuperAdmin || tenants.length > 1;
  const label = activeTenant?.name || 'Mandant wählen';

  if (!canSwitch) {
    return (
      <div
        className="mx-3 mt-3 mb-1 flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5"
        data-testid="tenant-badge"
      >
        <Building2 className="h-4 w-4 text-brand-red" strokeWidth={1.5} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[10px] uppercase tracking-wide text-white/50">
            Mandant
          </p>
          <p className="truncate text-xs font-semibold text-white">{label}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-3 mt-3 mb-1">
      <DropdownMenu>
        <DropdownMenuTrigger
          data-testid="tenant-switcher-trigger"
          className={cn(
            'group w-full flex items-center gap-2 rounded-md border border-white/15 bg-white/5',
            'px-2.5 py-1.5 text-left transition-colors hover:bg-white/10 focus:outline-none',
            'focus-visible:ring-2 focus-visible:ring-brand-red',
          )}
        >
          <Building2 className="h-4 w-4 text-brand-red" strokeWidth={1.5} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[10px] uppercase tracking-wide text-white/50">
              {isSuperAdmin ? 'Super-Admin · Mandant' : 'Mandant'}
            </p>
            <p className="truncate text-xs font-semibold text-white">{label}</p>
          </div>
          <ChevronsUpDown className="h-3.5 w-3.5 text-white/40 shrink-0" strokeWidth={2} />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="start"
          className="w-64"
          data-testid="tenant-switcher-menu"
        >
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-brand-muted">
            Mandant wechseln
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {tenants.map((t) => {
            const isActive = activeTenant?.id === t.id;
            return (
              <DropdownMenuItem
                key={t.id}
                data-testid={`tenant-option-${t.slug || t.id}`}
                disabled={isActive}
                onClick={() => !isActive && switchTenant(t.id)}
                className="flex items-center gap-2 py-2"
              >
                <Building2 className="h-4 w-4 text-brand-muted shrink-0" strokeWidth={1.5} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{t.name}</p>
                  {t.slug && (
                    <p className="truncate text-[11px] text-brand-muted">{t.slug}</p>
                  )}
                </div>
                {isActive && <Check className="h-4 w-4 text-brand-red shrink-0" />}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
